/**
 * Functions for working with 3 dimensional line segments
 * @module
 */
import { DIST_EPSILON } from "./core";
import { vec2Dist } from "./vec2";
import { vec3Dist, vec3Dot, vec3Lerp, vec3Normalize, vec3Result, vec3Scale } from "./vec3";

export const line3Len = vec3Dist;

export type Line3Segment = [number, number, number, number, number, number]; // [x0, y0, z0, x1, y1, z1]

export function line3Tangent(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  negate: boolean = false,
) {
  if (negate) {
    return vec3Normalize(x0 - x1, y0 - y1, z0 - z1);
  } else {
    return vec3Normalize(x1 - x0, y1 - y0, z1 - z0);
  }
}

export function line3Eval(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  t: number,
  normalized: boolean = true,
) {
  if (normalized) {
    return vec3Lerp(x0, y0, z0, x1, y1, z1, t);
  }

  const [dirx, diry, dirz] = line3Tangent(x0, y0, z0, x1, y1, z1);

  vec3Result[0] = x0 + dirx * t;
  vec3Result[1] = y0 + diry * t;
  vec3Result[2] = z0 + dirz * t;

  return vec3Result;
}

export function line3ProjectPt(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  x: number,
  y: number,
  z: number,
) {
  const [dirx, diry, dirz] = line3Tangent(x0, y0, z0, x1, y1, z1);
  const dot = vec3Dot(dirx, diry, dirz, x - x0, y - y0, z - z0);
  const [scaledX, scaledY, scaledZ] = vec3Scale(dirx, diry, dirz, dot);
  return [x0 + scaledX, y0 + scaledY, z0 + scaledZ];
}

export function line3MidPt(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  return line3Eval(x0, y0, z0, x1, y1, z1, 0.5);
}

export function line3Slope(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  epsilon: number = DIST_EPSILON,
) {
  const run = vec2Dist(x0, y0, x1, y1);
  const rise = z1 - z0;
  if (Math.abs(run) < epsilon) {
    return rise > 0 ? Infinity : -Infinity;
  }
  return rise / run;
}
